<template>
  <div class="is-clearfix">
    <div class="is-pulled-left">
      <span v-t="'showing'" />
      <b-select v-model="pagination.perPage" size="is-small" style="display: inline">
        <option v-for="numItems in perPageOptions" :value="numItems" :key="numItems">
          {{ numItems }}
        </option>
      </b-select>
      <span v-t="'results_per_page'" />
    </div>

    <div class="field is-pulled-right">
      <b-field>
        <b-input v-model="filterQuery.term" type="search" icon="search" :placeholder="$t('search')" />
      </b-field>
    </div>

    <b-table
      ref="usersTable"
      :data="isLoading ? [] : users"
      :default-sort="['name', 'asc']"
      hoverable>
      <template slot-scope="props">
        <b-table-column field="name" :label="$t('table_columns.name')" sortable>
          {{ props.row.name }}
        </b-table-column>

        <b-table-column field="email" :label="$t('table_columns.email')" sortable>
          <em v-if="props.row.id === 1" class="has-text-grey">{{ props.row.email }}</em>
          <span v-else>{{ props.row.email }}</span>
        </b-table-column>

        <b-table-column field="enterprise" :label="$t('table_columns.enterprise')" sortable>
          {{ props.row.enterprise }}
        </b-table-column>

        <b-table-column field="group" :label="$t('table_columns.group')" sortable centered>
          {{ props.row.group }}
        </b-table-column>

        <b-table-column field="actions">
          <div class="has-text-right">
            <router-link :to="{ name: 'users-edit', params: { user: props.row.id } }"
              class="button is-small is-primary" :title="$t('edit_info')">
              <i class="fas fa-pencil-alt"></i>
            </router-link>
            &nbsp;
            <button
              :title="$t('delete_info')"
              v-if="props.row.group !== 'Admin'"
              @click.prevent="destroy($event, props.row.id)"
              type="button" class="button is-small is-danger">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </b-table-column>
      </template>

      <template slot="empty">
        <section class="section">
          <loading-content v-if="isLoading" />
          <nothing-found v-else />
        </section>
      </template>
    </b-table>

    <b-pagination
      :total="pagination.total"
      :current.sync="pagination.current"
      :per-page="pagination.perPage"
      order="is-centered" />
  </div>
</template>

<script>
import API from '@/api'
import Utils from '@/utils'
import NothingFound from './NothingFound'
import LoadingContent from './LoadingContent'

// Delay para a busca através de requisição AJAX.
// Declarado fora do escopo do Vue por ser apenas uma variável utilitária.
let delayTimer

export default {
  name: 'UsersIndex',
  components: {
    NothingFound,
    LoadingContent
  },
  data () {
    return {
      filterQuery: {
        attrs: ['name', 'email', 'enterprise', 'group'],
        term: ''
      },
      pagination: {
        total: 0,
        current: 1,
        perPage: 15
      },
      perPageOptions: [15, 30, 60, 100],
      isLoading: false,
      users: []
    }
  },
  mounted () {
    this.fetchAll()
  },
  watch: {
    // Atualiza a listagem sempre que não for uma rota de cadastro/edição.
    '$route' ({ name }) {
      if (name === 'users') {
        this.fetchAll()
      }
    },
    // Atualiza a listagem sempre que for alterada a quantidade de itens por página.
    'pagination.perPage' () {
      this.fetchAll()
    },
    // Atualiza a listagem sempre que a página (paginação) for alterada.
    'pagination.current' () {
      this.fetchAll()
    },
    // Atualiza a listagem sempre um termo de pesquisa for inserido.
    'filterQuery.term' () {
      // O termo de pesquisa precisa ter pelo menos 3 caracteres
      if (this.filterQuery.term.length >= 3) {
      // A pesquisa só será feita depois de um delay de 300 milisegundos
        clearTimeout(delayTimer)
        delayTimer = setTimeout(() => {
          this.fetchAll()
        }, 300)
      }
    }
  },
  methods: {
    // Busca por todos os usuários já cadastrados no sistema.
    fetchAll () {
      this.isLoading = true

      // Parâmetros padrão da consulta
      let params = {
        take: this.pagination.perPage,
        page: this.pagination.current
      }

      // Filtro de pesquisa (caso exista)
      if (this.filterQuery.term.length > 0) {
        params.search_term = '%' + this.filterQuery.term + '%'
        params.search_attrs = this.filterQuery.attrs
        params.search_chain = 'or'
      }

      API.users.index(params, (users, pagination) => {
        // Lista de usuários
        this.users = users

        // Paginação
        this.pagination.total = pagination.total
        this.pagination.perPage = pagination.per_page
        this.pagination.current = pagination.current_page

        // Ordenação da tabela
        this.$refs.usersTable.initSort()

        this.isLoading = false
      })
    },

    // Remove o usuário com base em sua ID
    destroy (event, id) {
      let row = event.target.closest('tr')
      let name = this.users.find(x => x.id === id).name

      Utils.alertConfirmation(
        this.$t('delete_alert', { name }),
        toast => {
          API.users.destroy(id, () => {
            this.$snotify.remove(toast.id)
            row.style.background = '#ff0'
            Utils.fadeOut(row, 630, () => row.remove())
          })
        },
        toast => this.$snotify.remove(toast.id),
        this.$snotify
      )
    }
  }
}
</script>

<i18n>
{
  "en-US": {
    "table_columns": {
      "name": "Name",
      "email": "Email",
      "enterprise": "Enterprise",
      "group": "Group"
    },
    "search": "Search",
    "edit_info": "Edit user",
    "delete_info": "Remove user",
    "delete_alert": "Are you sure you want to remove user {name}?",
    "showing": "Showing ",
    "results_per_page": " results per page"
  },
  "pt-BR": {
    "table_columns": {
      "name": "Nome",
      "email": "E-mail",
      "enterprise": "Empresa",
      "group": "Grupo"
    },
    "search": "Pesquisar",
    "edit_info": "Editar usuário",
    "delete_info": "Remover usuário",
    "delete_alert": "Tem certeza que deseja remover o usuário {name}?",
    "showing": "Exibindo ",
    "results_per_page": " resultados por página"
  }
}
</i18n>
